export type ChartResolution = 'native' | '1D';
export type ChartDataKind = 'equity' | 'symbol';
export type ChartDisplayMode = 'dollar' | 'percent' | 'gross';
export type ChartDataPoint = Record<string, any>;

export const isIntradayTimeframe = (timeframe?: string): boolean => {
  const normalized = String(timeframe ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (normalized === 'minute' || normalized === 'hour') return true;
  return /^\d+\s*(?:m|min|mins|minute|minutes|h|hr|hrs|hour|hours)$/.test(normalized);
};

export const shouldShowBacktestTradeMarkers = (
  resolution: ChartResolution,
  timeframe?: string,
): boolean => resolution === 'native' && isIntradayTimeframe(timeframe);

export interface ChartDataPreparationOptions {
  data: ChartDataPoint[];
  lowerPaneData: ChartDataPoint[];
  type: ChartDataKind;
  resolution: ChartResolution;
  showEquityCandles: boolean;
  displayMode: ChartDisplayMode;
}

export interface PreparedChartData {
  data: ChartDataPoint[];
  lowerPaneData: ChartDataPoint[];
}

export const toDayString = (value: string): string => {
  if (!value) return '';
  if (value.includes('T')) return value.split('T')[0];
  if (value.includes(' ')) return value.split(' ')[0];
  return value;
};

export const prepareChartData = ({
  data,
  lowerPaneData,
  type,
  resolution,
  showEquityCandles,
  displayMode,
}: ChartDataPreparationOptions): PreparedChartData => {
  let processedData = [...data];
  let processedLowerPaneData = [...lowerPaneData];

  if (resolution === '1D') {
    const dailyMap: Record<string, ChartDataPoint> = {};
    data.forEach((item) => {
      const day = toDayString(item.date);
      if (!dailyMap[day]) {
        dailyMap[day] = { ...item, date: day };
        dailyMap[day].equity_open = item.equity;
        dailyMap[day].equity_high = item.equity;
        dailyMap[day].equity_low = item.equity;
        dailyMap[day].equity_close = item.equity;
        if (type === 'symbol') {
          dailyMap[day].high = item.high;
          dailyMap[day].low = item.low;
          dailyMap[day].open = item.open;
          dailyMap[day].close = item.close;
          dailyMap[day].ma30 = item.ma30 ?? null;
          dailyMap[day].ma200 = item.ma200 ?? null;
        }
      } else {
        const aggregate = dailyMap[day];
        aggregate.equity = item.equity;
        aggregate.equity_high = Math.max(
          aggregate.equity_high !== undefined ? aggregate.equity_high : aggregate.equity,
          item.equity,
        );
        aggregate.equity_low = Math.min(
          aggregate.equity_low !== undefined ? aggregate.equity_low : aggregate.equity,
          item.equity,
        );
        aggregate.equity_close = item.equity;

        if (type === 'equity') {
          Object.keys(item).forEach((key) => {
            if (key.startsWith('equity_')) aggregate[key] = item[key];
          });
        } else if (type === 'symbol') {
          aggregate.high = Math.max(aggregate.high, item.high);
          aggregate.low = Math.min(aggregate.low, item.low);
          aggregate.close = item.close;
          if (item.ma30 != null) aggregate.ma30 = item.ma30;
          if (item.ma200 != null) aggregate.ma200 = item.ma200;
        }
      }
    });

    processedData = Object.values(dailyMap).sort((left, right) => left.date.localeCompare(right.date));
    if (type === 'symbol') {
      processedData = processedData.map((point, index, points) => {
        const closes = points
          .slice(0, index + 1)
          .map((item) => Number(item.close))
          .filter(Number.isFinite);
        const average = (period: number) => closes.length < period
          ? null
          : closes.slice(-period).reduce((sum, value) => sum + value, 0) / period;
        return { ...point, ma30: average(30), ma200: average(200) };
      });
    }

    if (lowerPaneData.length > 0) {
      const dailyLowerMap: Record<string, ChartDataPoint> = {};
      lowerPaneData.forEach((item) => {
        const day = toDayString(item.date);
        dailyLowerMap[day] = { ...item, date: day };
      });
      processedLowerPaneData = Object.values(dailyLowerMap)
        .sort((left, right) => left.date.localeCompare(right.date));
    }
  }

  if (showEquityCandles) {
    processedData = processedData.map((point) => ({
      ...point,
      open: point.equity_open !== undefined ? point.equity_open : point.equity,
      high: point.equity_high !== undefined ? point.equity_high : point.equity,
      low: point.equity_low !== undefined ? point.equity_low : point.equity,
      close: point.equity_close !== undefined ? point.equity_close : point.equity,
    }));
  }

  if (type === 'equity' && displayMode === 'percent' && processedData.length > 0) {
    const startEquity = processedData[0].equity || 1;
    const strategyStarts: Record<string, number> = {};

    processedData.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key.startsWith('equity_') && key !== 'equity_curve') {
          if (!strategyStarts[key] && point[key] && point[key] !== 0) {
            strategyStarts[key] = point[key];
          }
        }
      });
    });

    processedData = processedData.map((point) => {
      const normalized = { ...point };
      normalized.equity = ((point.equity / startEquity) - 1) * 100;
      Object.keys(point).forEach((key) => {
        if (key.startsWith('equity_') && key !== 'equity_curve') {
          const start = strategyStarts[key] || 1;
          normalized[key] = ((point[key] / start) - 1) * 100;
        }
      });
      return normalized;
    });
  }

  return { data: processedData, lowerPaneData: processedLowerPaneData };
};
